import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2, XCircle, ShieldCheck } from 'lucide-react'
import api from '@/consumer/lib/api'
import { setAuthToken } from '@/shared/lib/auth-token'
import { routeAfterAuth } from '@/consumer/lib/post-auth'
import { rememberTenantSlug } from '@/consumer/lib/tenant-slug'
import { getTenantSlugFromHost } from '@/shared/lib/tenant-host'

// Module-scoped guard: magic-link tokens are single-use, and React StrictMode
// mounts each component twice in dev - a component-local ref is not enough.
const verifiedTokens = new Set<string>()

export default function VerifyPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<'verifying' | 'error'>('verifying')

  const token = searchParams.get('token')
  const fallbackNext = searchParams.get('next') || ''
  // The tenant subdomain (`<slug>.domain`) is authoritative; the `?tenant=`
  // param only matters on a generic host or a pre-subdomain magic link.
  const fallbackTenant = getTenantSlugFromHost() || searchParams.get('tenant') || ''

  // Remember the tenant from the magic-link so any later slug-less visit
  // (e.g. an expired-link bounce to /login) stays on the right brand.
  useEffect(() => {
    if (fallbackTenant) rememberTenantSlug(fallbackTenant)
  }, [fallbackTenant])

  useEffect(() => {
    if (!token) {
       
      setStatus('error')
      return
    }

    if (verifiedTokens.has(token)) return
    verifiedTokens.add(token)

    api
      .post('/auth/verify/', { token })
      .then(async (res) => {
        // Persist the session token so subsequent API calls carry the
        // X-Session-Token header (no cookies behind Firebase).
        if (res.data?.session_token) {
          setAuthToken(res.data.session_token)
        }
        await queryClient.invalidateQueries({ queryKey: ['consumer-session'] })
        routeAfterAuth(res.data, navigate)
      })
      .catch(() => {
        setStatus('error')
      })
  }, [token, navigate, queryClient])

  const handleRequestNewLink = () => {
    const params = new URLSearchParams()
    if (fallbackNext) params.set('next', fallbackNext)
    if (fallbackTenant) params.set('tenant', fallbackTenant)
    const target = params.toString() ? `/login?${params.toString()}` : '/login'
    navigate(target, { replace: true })
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--bg)] px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div
            className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-[8px] bg-[var(--green)]"
          >
            <ShieldCheck className="h-7 w-7 text-[var(--bg)]" />
          </div>
        </div>

        <div
          className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-8 text-center"
        >
          {status === 'verifying' ? (
            <>
              <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-[var(--green)]" />
              <h2 className="clara-heading mb-2">
                {t('verify.loading', 'Verifying your link')}
              </h2>
              <p className="text-sm text-[var(--ink-3)]">
                {t('verify.loadingDescription', 'Please wait while we sign you in...')}
              </p>
            </>
          ) : (
            <>
              <div
                className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-[var(--status-error-bg)]"
              >
                <XCircle className="h-8 w-8 text-[var(--status-error)]" />
              </div>
              <h2 className="clara-heading mb-2">
                {t('verify.expired', 'Link expired or invalid')}
              </h2>
              <p className="mb-6 text-sm text-[var(--ink-3)]">
                {t(
                  'verify.expiredDescription',
                  'This sign-in link is no longer valid. Please request a new one.',
                )}
              </p>
              <button
                onClick={handleRequestNewLink}
                className="w-full cursor-pointer rounded-[4px] bg-[var(--green)] px-4 py-3 text-sm font-normal text-[var(--bg)] transition-opacity hover:opacity-90"
              >
                {t('verify.requestNewLink', 'Request new link')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
